// Select — custom listbox dropdown. Replaces a native <select> so the
// open menu uses our tokens instead of the OS chrome. Keeps the same
// API surface (label / hint / options / placeholder / value / onChange)
// so existing call-sites that read e.target.value keep working.
//
// Keyboard:
//   Enter / Space / ArrowDown / ArrowUp / "any printable" → open
//   ArrowUp / ArrowDown / Home / End                     → move highlight
//   Enter                                                → commit highlight
//   Esc / Tab / click-outside                            → close
//   typing                                               → jump to next option
//                                                          whose label starts
//                                                          with the buffer
//                                                          (buffer resets after
//                                                          ~500ms idle)
import * as React from "react";
import { cn } from "@/lib/cn";

export interface SelectOption {
  value: string;
  label: string;
  /**
   * A mark drawn before the label, in the menu AND on the closed
   * trigger — a currency's artwork, say. Decorative by contract: the
   * label alone must still identify the option, because type-to-jump
   * matches on the label and a screen reader never sees this.
   */
  icon?: React.ReactNode;
}

export interface SelectProps {
  label?: string;
  hint?: string;
  options: SelectOption[];
  // placeholder, when set, is rendered as the first option with an
  // empty value — selecting it un-applies the filter.
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  // Same shape as the native <select>'s onChange so callers reading
  // e.target.value keep working without migration.
  onChange?: (e: { target: { value: string } }) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
  name?: string;
  /**
   * Names the control when `label` would be visually redundant — an
   * inline unit picker sitting beside the number it qualifies, say.
   * Ignored when `label` is set, which already provides the name.
   */
  "aria-label"?: string;
}

export function Select({
  label,
  hint,
  options,
  placeholder,
  value,
  defaultValue,
  onChange,
  disabled,
  id,
  className,
  name,
  "aria-label": ariaLabel,
}: SelectProps) {
  const selectId = id ?? React.useId();
  const listboxId = `${selectId}-listbox`;
  // Custom comboboxes shouldn't open on label click — htmlFor would fire
  // a synthetic click on the trigger button. Use aria-labelledby so the
  // label still names the field for screen readers without that side
  // effect.
  const labelId = `${selectId}-label`;
  const [open, setOpen] = React.useState(false);
  // Internal state for uncontrolled mode.
  const [internalValue, setInternalValue] = React.useState(defaultValue ?? "");
  const isControlled = value !== undefined;
  const current = isControlled ? value : internalValue;

  // Flatten options into a single visit list, prepending the placeholder
  // pseudo-option when set so it can be highlighted/selected like any other.
  const items = React.useMemo<SelectOption[]>(() => {
    return placeholder !== undefined
      ? [{ value: "", label: placeholder }, ...options]
      : options;
  }, [options, placeholder]);

  const selectedIndex = items.findIndex((o) => o.value === current);
  const [highlight, setHighlight] = React.useState<number>(
    selectedIndex >= 0 ? selectedIndex : 0,
  );

  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const searchBufRef = React.useRef<string>("");
  const searchTimerRef = React.useRef<number | null>(null);

  // Sync highlight with the current selection and move focus into the
  // listbox whenever the menu opens — without focus the onKeyDown
  // handler on the <ul> never fires.
  React.useEffect(() => {
    if (open) {
      setHighlight(selectedIndex >= 0 ? selectedIndex : 0);
      // Defer so the <ul> is mounted before we focus it.
      const id = window.setTimeout(() => listRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open, selectedIndex]);

  // Scroll the highlighted option into view as it moves.
  React.useEffect(() => {
    if (!open) return;
    const li = listRef.current?.children[highlight] as HTMLElement | undefined;
    li?.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  // Close on outside click and on Escape from anywhere.
  React.useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [open]);

  const commit = (next: string) => {
    if (!isControlled) setInternalValue(next);
    onChange?.({ target: { value: next } });
    setOpen(false);
    // Return focus to the trigger so keyboard nav can continue.
    triggerRef.current?.focus();
  };

  const selectedOption = items.find((o) => o.value === current);
  const triggerLabel = selectedOption?.label ?? placeholder ?? "";

  const jumpToSearch = (char: string) => {
    // Buffer printable keystrokes so e.g. typing "ca" jumps to "Canceled"
    // even if there's also "Closed" — the longer the typed prefix the
    // more specific the match. Reset after a brief idle.
    if (searchTimerRef.current !== null) {
      window.clearTimeout(searchTimerRef.current);
    }
    searchBufRef.current = (searchBufRef.current + char).toLowerCase();
    const buf = searchBufRef.current;
    const idx = items.findIndex((o) => o.label.toLowerCase().startsWith(buf));
    if (idx >= 0) setHighlight(idx);
    searchTimerRef.current = window.setTimeout(() => {
      searchBufRef.current = "";
      searchTimerRef.current = null;
    }, 500);
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (
      e.key === "ArrowDown" ||
      e.key === "ArrowUp" ||
      e.key === "Enter" ||
      e.key === " "
    ) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      // Type-to-jump while closed: open the menu and start the buffer.
      setOpen(true);
      jumpToSearch(e.key);
    }
  };

  const onListKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (!open) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlight((h) => Math.min(items.length - 1, h + 1));
        return;
      case "ArrowUp":
        e.preventDefault();
        setHighlight((h) => Math.max(0, h - 1));
        return;
      case "Home":
        e.preventDefault();
        setHighlight(0);
        return;
      case "End":
        e.preventDefault();
        setHighlight(items.length - 1);
        return;
      case "Enter":
      case " ":
        e.preventDefault();
        commit(items[highlight].value);
        return;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        return;
      case "Tab":
        // Let Tab close the menu but still advance focus naturally.
        setOpen(false);
        return;
    }
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      jumpToSearch(e.key);
    }
  };

  // Visible state styling.
  const isPlaceholder = current === "" && placeholder !== undefined;

  return (
    <div className="flex flex-col gap-1.5" ref={rootRef}>
      {label && (
        <span
          id={labelId}
          className="text-2xs font-semibold uppercase tracking-wide text-t-3"
        >
          {label}
        </span>
      )}
      <div className="relative">
        <button
          type="button"
          id={selectId}
          ref={triggerRef}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-labelledby={label ? labelId : undefined}
          aria-label={label ? undefined : ariaLabel}
          aria-disabled={disabled || undefined}
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
          onKeyDown={onTriggerKeyDown}
          className={cn(
            "flex h-10 w-full items-center justify-between gap-2 rounded-md border border-line-1 bg-bg-2 px-3 text-left text-sm",
            "transition-colors focus:border-line-3 focus:outline-none focus:ring-4 focus:ring-neon/15",
            disabled && "cursor-not-allowed opacity-50",
            className,
          )}
        >
          <span
            className={cn(
              "flex min-w-0 items-center gap-1.5",
              isPlaceholder ? "text-t-4" : "text-t-1",
            )}
          >
            {selectedOption?.icon}
            <span className="truncate">{triggerLabel || " "}</span>
          </span>
          <svg
            aria-hidden="true"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            className={cn(
              "shrink-0 text-t-3 transition-transform",
              open && "rotate-180",
            )}
          >
            <path
              d="M3 4.5l3 3 3-3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </button>

        {/* Hidden native input so this Select works inside a <form>
            even though the trigger is a button. */}
        {name && <input type="hidden" name={name} value={current} />}

        {open && (
          <ul
            id={listboxId}
            ref={listRef}
            role="listbox"
            tabIndex={-1}
            aria-activedescendant={
              items[highlight] ? `${selectId}-opt-${highlight}` : undefined
            }
            onKeyDown={onListKeyDown}
            className={cn(
              "absolute left-0 right-0 z-30 mt-1 max-h-64 overflow-y-auto rounded-md border border-line-1 bg-bg-2 py-1 shadow-card",
              "focus:outline-none",
            )}
            style={{ minWidth: "100%" }}
          >
            {items.map((opt, i) => {
              const selected = opt.value === current;
              const highlighted = i === highlight;
              return (
                <li
                  key={`${opt.value}-${i}`}
                  id={`${selectId}-opt-${i}`}
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => {
                    // mousedown (not click) so the trigger doesn't lose
                    // focus before we commit — Safari occasionally beats
                    // us to the close otherwise.
                    e.preventDefault();
                    commit(opt.value);
                  }}
                  className={cn(
                    "flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-sm",
                    highlighted ? "bg-neon/10 text-t-1" : "text-t-2",
                    selected && "font-medium text-t-1",
                  )}
                >
                  <span className="flex min-w-0 items-center gap-1.5">
                    {opt.icon}
                    <span className="truncate">{opt.label}</span>
                  </span>
                  {selected && (
                    <svg
                      aria-hidden="true"
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      className="shrink-0 text-neon"
                    >
                      <path
                        d="M2.5 6.5l2.5 2.5 4.5-5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {hint && <p className="text-2xs text-t-3">{hint}</p>}
    </div>
  );
}
